import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, List, ListItem, ListItemText, Paper,
  CircularProgress, Alert, Button, Divider, Chip, Avatar, Pagination,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, IconButton,
  ListItemAvatar, ListItemSecondaryAction, Tabs, Tab, useTheme,
  Collapse, ListItemButton
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import messageService from '../services/messageService';
import { useAuth } from '../contexts/AuthContext';
import {
  Person, Business, Search, Message as MessageIcon, MarkEmailRead,
  MarkEmailUnread, Delete, DeleteSweep, FilterList, Reply, Close,
  Tune, ExpandMore, ExpandLess, AllInbox, MarkunreadMailbox, CalendarToday, OpenInNew
} from '@mui/icons-material';

const Messages = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const autoOpenRef = useRef(location.state?.autoOpen || null);
  const [conversations, setConversations] = useState([]); // Üzenetek helyett beszélgetések
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  
  // Lapozás és szűrők
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState('');       // input értéke
  const [appliedSearch, setAppliedSearch] = useState(''); // csak Enter/gomb után aktiválódik
  const [tabValue, setTabValue] = useState(0); // 0: Összes, 1: Olvasatlan

  // Tömeges műveletek
  const [selectedConversationIds, setSelectedConversationIds] = useState(new Set()); // Beszélgetés ID-k
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');

  // Mobil szűrő panel
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Beszélgetés megtekintése/válasz dialog
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState([]); // Az aktuális beszélgetés üzenetei
  const [currentParticipant, setCurrentParticipant] = useState(null); // A beszélgetés másik résztvevője
  const [replyContent, setReplyContent] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const messagesEndRef = useRef(null); // Scroll a legújabb üzenetre

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: 15,
        ...(appliedSearch && { search: appliedSearch }),
      };

      if (tabValue === 1) params.isRead = false;

      const response = await messageService.getMessages(params.page, params.limit, params);

      if (response.success && response.data) {
        const allMessages = response.data;
        const groupedConversations = groupMessagesIntoConversations(allMessages, user.id);

        let filteredConversations = groupedConversations;
        if (tabValue === 1) filteredConversations = filteredConversations.filter(conv => conv.unreadCount > 0);

        const limit = 15;
        const totalConversations = filteredConversations.length;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedConversations = filteredConversations.slice(startIndex, endIndex);

        setConversations(paginatedConversations);
        setPagination({
          total: totalConversations,
          pages: Math.ceil(totalConversations / limit),
          page: page,
          limit: limit
        });
      } else {
        throw new Error(response.message || 'Beszélgetések betöltése sikertelen.');
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
      setError(err.message || 'Beszélgetések betöltése sikertelen.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page, appliedSearch, tabValue, user.id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadConversations();
  }, [isAuthenticated, loadConversations, navigate]);

  // Segédfüggvény az üzenetek beszélgetésekké csoportosításához
  const groupMessagesIntoConversations = (messages, currentUserId) => {
    const conversationsMap = new Map();

    messages.forEach(msg => {
      const participant = msg.sender.id === currentUserId ? msg.recipient : msg.sender;
      const conversationKey = participant.id;

      if (!conversationsMap.has(conversationKey)) {
        conversationsMap.set(conversationKey, {
          participant: participant,
          messages: [],
          unreadCount: 0,
          latestMessage: null,
        });
      }

      const conversation = conversationsMap.get(conversationKey);
      conversation.messages.push(msg);
      if (!msg.isRead && msg.recipient.id === currentUserId) {
        conversation.unreadCount++;
      }
      if (!conversation.latestMessage || new Date(msg.createdAt) > new Date(conversation.latestMessage.createdAt)) {
        conversation.latestMessage = msg;
      }
    });

    // Rendezés a legutóbbi üzenet dátuma szerint
    return Array.from(conversationsMap.values()).sort((a, b) => 
      new Date(b.latestMessage.createdAt) - new Date(a.latestMessage.createdAt)
    );
  };

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setAppliedSearch('');
    setPage(1);
    setTabValue(0);
    setSelectedConversationIds(new Set());
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
    setSelectedConversationIds(new Set());
  };

  const handleMarkConversationAsRead = async (participantId) => {
    try {
      // Backend végpont szükséges az összes üzenet olvasottá tételéhez egy beszélgetésben
      // Ideiglenesen lekérjük a beszélgetést és egyenként jelöljük olvasottnak
      const response = await messageService.getConversation(participantId);
      if (response.success && response.data) {
        const unreadMessages = response.data.filter(msg => !msg.isRead && msg.recipientId === user.id);
        for (const msg of unreadMessages) {
          await messageService.markMessageAsRead(msg.id);
        }
      }
      loadConversations();
      // Frissítjük az aktuális beszélgetést is, ha nyitva van
      if (currentParticipant && currentParticipant.id === participantId) {
        loadCurrentConversation(participantId);
      }
    } catch (err) {
      console.error("Failed to mark conversation as read:", err);
      setError('Művelet sikertelen');
    }
  };

  const handleMarkConversationAsUnread = async (participantId) => {
    try {
      // Backend végpont szükséges az összes üzenet olvasatlanná tételéhez egy beszélgetésben
      // Ideiglenesen lekérjük a beszélgetést és egyenként jelöljük olvasatlannak
      const response = await messageService.getConversation(participantId);
      if (response.success && response.data) {
        const readMessages = response.data.filter(msg => msg.isRead && msg.recipientId === user.id);
        for (const msg of readMessages) {
          await messageService.markMessageAsUnread(msg.id);
        }
      }
      loadConversations();
      // Frissítjük az aktuális beszélgetést is, ha nyitva van
      if (currentParticipant && currentParticipant.id === participantId) {
        loadCurrentConversation(participantId);
      }
    } catch (err) {
      console.error("Failed to mark conversation as unread:", err);
      setError('Művelet sikertelen');
    }
  };

  const handleDeleteConversation = async (participantId) => {
    try {
      // Backend végpont szükséges az összes üzenet törléséhez egy beszélgetésben
      // Ideiglenesen lekérjük a beszélgetést és egyenként töröljük
      const response = await messageService.getConversation(participantId);
      if (response.success && response.data) {
        for (const msg of response.data) {
          await messageService.deleteMessage(msg.id);
        }
      }
      loadConversations();
      if (currentParticipant && currentParticipant.id === participantId) {
        setConversationDialogOpen(false);
        setCurrentConversation([]);
        setCurrentParticipant(null);
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
      setError('Törlés sikertelen');
    }
  };

  const handleSelectConversation = (conversationId) => {
    setSelectedConversationIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conversationId)) {
        newSet.delete(conversationId);
      } else {
        newSet.add(conversationId);
      }
      return newSet;
    });
  };

  const handleSelectAllConversations = () => {
    if (selectedConversationIds.size === conversations.length) {
      setSelectedConversationIds(new Set());
    } else {
      setSelectedConversationIds(new Set(conversations.map(conv => conv.participant.id)));
    }
  };

  const handleBulkAction = async () => {
    try {
      const participantIds = Array.from(selectedConversationIds);
      
      switch (bulkAction) {
        case 'markRead':
          for (const id of participantIds) {
            await handleMarkConversationAsRead(id);
          }
          break;
        case 'markUnread':
          for (const id of participantIds) {
            await handleMarkConversationAsUnread(id);
          }
          break;
        case 'delete':
          for (const id of participantIds) {
            await handleDeleteConversation(id);
          }
          break;
        default:
          break;
      }
      
      setSelectedConversationIds(new Set());
      setBulkActionDialogOpen(false);
      loadConversations();
    } catch (error) {
      console.error('Tömeges művelet hiba:', error);
      setError('Tömeges művelet sikertelen');
    }
  };

  const loadCurrentConversation = useCallback(async (participantId) => {
    try {
      setLoading(true);
      const response = await messageService.getConversation(participantId);
      if (response.success && response.data) {
        setCurrentConversation(response.data);
        // Scroll a legújabb üzenetre
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        throw new Error(response.message || 'Beszélgetés betöltése sikertelen.');
      }
    } catch (err) {
      console.error("Error loading current conversation:", err);
      setError(err.message || 'Beszélgetés betöltése sikertelen.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenConversationDialog = (conversation) => {
    setCurrentParticipant(conversation.participant);
    setConversationDialogOpen(true);
    loadCurrentConversation(conversation.participant.id);
  };

  // Auto-open ha location.state.autoOpen van megadva (pl. Dashboard üzenet gombból)
  useEffect(() => {
    if (autoOpenRef.current && !loading) {
      handleOpenConversationDialog({ participant: autoOpenRef.current });
      autoOpenRef.current = null;
      navigate(location.pathname, { replace: true });
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseConversationDialog = () => {
    setConversationDialogOpen(false);
    setCurrentConversation([]);
    setCurrentParticipant(null);
    setReplyContent('');
    setReplyError(null);
    loadConversations(); // Frissítjük a beszélgetés listát
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      setReplyError('Az üzenet nem lehet üres.');
      return;
    }
    if (!currentParticipant) return;

    setReplyLoading(true);
    setReplyError(null);

    try {
      const response = await messageService.sendMessage(currentParticipant.id, replyContent);

      if (response.success) {
        setReplyContent('');
        setReplyLoading(false);
        setReplyError(null);
        loadCurrentConversation(currentParticipant.id); // Frissítjük az aktuális beszélgetést
      } else {
        throw new Error(response.message || 'Válasz küldése sikertelen.');
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      setReplyError(err.message || 'Válasz küldése sikertelen.');
      setReplyLoading(false);
    }
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalUnreadCount = conversations.reduce((acc, conv) => acc + conv.unreadCount, 0);

  if (!isAuthenticated) {
    return null;
  }

  if (loading && conversations.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Beszélgetések betöltése...
        </Typography>
      </Container>
    );
  }

  /* ── Sidebar tartalom (megosztott desktop és mobil között) ── */
  const sidebarContent = (
    <>
      {/* Cím */}
      <Box sx={{ px: 1, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
            Üzeneteim
          </Typography>
          {totalUnreadCount > 0 && (
            <Chip
              label={totalUnreadCount}
              color="primary"
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {pagination.total || 0} beszélgetés
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Navigáció */}
      <List dense disablePadding sx={{ mb: 1 }}>
        <ListItemButton
          selected={tabValue === 0}
          onClick={() => { setTabValue(0); setPage(1); setSelectedConversationIds(new Set()); }}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }
          }}
        >
          <AllInbox sx={{ mr: 1.5, fontSize: 20 }} />
          <ListItemText primary="Összes" />
          <Chip
            label={pagination.total || 0}
            size="small"
            sx={{
              height: 18, fontSize: '0.65rem',
              bgcolor: tabValue === 0 ? 'rgba(255,255,255,0.25)' : 'action.hover',
              color: tabValue === 0 ? 'white' : 'text.secondary'
            }}
          />
        </ListItemButton>

        <ListItemButton
          selected={tabValue === 1}
          onClick={() => { setTabValue(1); setPage(1); setSelectedConversationIds(new Set()); }}
          sx={{
            borderRadius: 2,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }
          }}
        >
          <MarkunreadMailbox sx={{ mr: 1.5, fontSize: 20 }} />
          <ListItemText primary="Olvasatlan" />
          {totalUnreadCount > 0 && (
            <Chip
              label={totalUnreadCount}
              color={tabValue === 1 ? 'default' : 'primary'}
              size="small"
              sx={{
                height: 18, fontSize: '0.65rem',
                bgcolor: tabValue === 1 ? 'rgba(255,255,255,0.25)' : undefined,
                color: tabValue === 1 ? 'white' : undefined
              }}
            />
          )}
        </ListItemButton>
      </List>

      <Divider sx={{ my: 1.5 }} />

      {/* Keresés */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          placeholder="Keresés..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" variant="contained" onClick={handleSearch} disabled={loading} fullWidth>
            Keresés
          </Button>
          {appliedSearch && (
            <Button size="small" variant="outlined" onClick={handleClearFilters} disabled={loading}>
              Törlés
            </Button>
          )}
        </Box>
      </Box>

    </>
  );

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: 'calc(100vh - 64px)', py: 3 }}>
      <Container maxWidth="lg">
        {/* Hiba megjelenítése */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

          {/* ── Desktop sidebar ── */}
          <Paper
            sx={{
              width: 260,
              flexShrink: 0,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              p: 2,
              position: 'sticky',
              top: 80,
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {sidebarContent}
          </Paper>

          {/* ── Fő tartalom (lista) ── */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>

            {/* Mobil fejléc */}
            <Box sx={{ display: { md: 'none' }, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                    Üzeneteim
                  </Typography>
                  {totalUnreadCount > 0 && (
                    <Chip label={totalUnreadCount} color="primary" size="small" sx={{ height: 20 }} />
                  )}
                </Box>
                <Button
                  size="small"
                  variant={mobileFiltersOpen ? 'contained' : 'outlined'}
                  startIcon={<Tune />}
                  onClick={() => setMobileFiltersOpen(prev => !prev)}
                >
                  Szűrők
                </Button>
              </Box>

              {/* Mobil tab gombok */}
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ bgcolor: 'background.paper', borderRadius: 2, minHeight: 36 }}
              >
                <Tab label="Összes" sx={{ minHeight: 36, py: 0.5 }} />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Olvasatlan
                      {totalUnreadCount > 0 && (
                        <Chip label={totalUnreadCount} color="primary" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                  }
                  sx={{ minHeight: 36, py: 0.5 }}
                />
              </Tabs>
            </Box>

            {/* Mobil szűrő panel */}
            <Collapse in={mobileFiltersOpen} sx={{ display: { md: 'none' } }}>
              <Paper sx={{ p: 2, mb: 1.5, bgcolor: 'background.paper' }}>
                {/* Csak a keresés és tömeges műveletek — a tabs mobil fejlécben van */}
                <TextField
                  placeholder="Keresés feladó/üzenet szerint..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={handleSearch} disabled={loading}>
                    Keresés
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleClearFilters} disabled={loading}>
                    Törlés
                  </Button>
                </Box>

              </Paper>
            </Collapse>

            {/* Üres állapot */}
            {conversations.length === 0 && !loading ? (
              <Paper sx={{ bgcolor: '#fff' }}>
                <Box textAlign="center" py={8}>
                  <MessageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {appliedSearch
                      ? 'Nincs találat a keresési feltételeknek megfelelően'
                      : tabValue === 1
                        ? 'Nincsenek olvasatlan beszélgetések'
                        : 'Még nincsenek beszélgetések'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {appliedSearch
                      ? 'Próbálja meg módosítani a keresési feltételeket'
                      : 'Az üzenetek itt fognak megjelenni'
                    }
                  </Typography>
                  {appliedSearch && (
                    <Button variant="outlined" onClick={handleClearFilters}>
                      Szűrők törlése
                    </Button>
                  )}
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ bgcolor: '#fff' }}>
                {/* Lista fejléc: kijelölés + tömeges műveletek */}
                {conversations.length > 0 && (
                  <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedConversationIds.size === conversations.length && conversations.length > 0}
                          indeterminate={selectedConversationIds.size > 0 && selectedConversationIds.size < conversations.length}
                          onChange={handleSelectAllConversations}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Összes kijelölése ({conversations.length})
                        </Typography>
                      }
                      sx={{ mr: 0 }}
                    />
                    {selectedConversationIds.size > 0 && (
                      <>
                        <Chip
                          label={`${selectedConversationIds.size} kijelölve`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                        <IconButton
                          size="small"
                          title="Olvasottnak jelölés"
                          onClick={() => { setBulkAction('markRead'); setBulkActionDialogOpen(true); }}
                        >
                          <MarkEmailRead sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Olvasatlannak jelölés"
                          onClick={() => { setBulkAction('markUnread'); setBulkActionDialogOpen(true); }}
                        >
                          <MarkEmailUnread sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          title="Törlés"
                          onClick={() => { setBulkAction('delete'); setBulkActionDialogOpen(true); }}
                        >
                          <DeleteSweep sx={{ fontSize: 18 }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                )}

                <List sx={{ p: 0 }}>
                  {conversations.map((conv, index) => (
                    <React.Fragment key={conv.participant.id}>
                      <ListItem
                        button
                        onClick={() => handleOpenConversationDialog(conv)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          bgcolor: conv.unreadCount > 0 ? 'rgba(42,93,50,0.04)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(42,93,50,0.08)' },
                          alignItems: 'flex-start'
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedConversationIds.has(conv.participant.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectConversation(conv.participant.id);
                              }}
                              size="small"
                            />
                          }
                          label=""
                          sx={{ mr: 0.5, mt: 0.5, alignSelf: 'flex-start' }}
                        />

                        <ListItemAvatar sx={{ mt: 0.5 }}>
                          <Avatar
                            src={conv.participant.avatarUrl || undefined}
                            sx={{
                              bgcolor: conv.unreadCount > 0 ? 'primary.main' : 'grey.400',
                              color: 'white',
                              width: 42,
                              height: 42
                            }}
                          >
                            {!conv.participant.avatarUrl && (
                              conv.participant.userType === 'PROVIDER' && conv.participant.provider
                                ? conv.participant.provider.businessName.charAt(0)
                                : conv.participant.firstName.charAt(0)
                            )}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          sx={{ my: 0, pr: { xs: '110px', md: '110px' } }}
                          primary={
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: conv.unreadCount > 0 ? 700 : 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mb: 0.25
                              }}
                            >
                              {conv.participant.userType === 'PROVIDER' && conv.participant.provider
                                ? conv.participant.provider.businessName
                                : `${conv.participant.firstName} ${conv.participant.lastName}`}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontWeight: conv.unreadCount > 0 ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}
                            >
                              {conv.latestMessage.content.length > 80
                                ? conv.latestMessage.content.substring(0, 80) + '...'
                                : conv.latestMessage.content}
                            </Typography>
                          }
                        />

                        <ListItemSecondaryAction sx={{ top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                            {formatMessageDate(conv.latestMessage.createdAt)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            <Box sx={{ position: 'relative' }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  conv.unreadCount > 0
                                    ? handleMarkConversationAsRead(conv.participant.id)
                                    : handleMarkConversationAsUnread(conv.participant.id);
                                }}
                                title={conv.unreadCount > 0 ? 'Olvasottnak jelölés' : 'Olvasatlannak jelölés'}
                                sx={{ color: 'text.secondary' }}
                              >
                                {conv.unreadCount > 0 ? <MarkEmailRead sx={{ fontSize: 18 }} /> : <MarkEmailUnread sx={{ fontSize: 18 }} />}
                              </IconButton>
                              {conv.unreadCount > 0 && (
                                <Chip
                                  label={conv.unreadCount}
                                  color="primary"
                                  size="small"
                                  sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    height: 16,
                                    fontSize: '0.6rem',
                                    minWidth: 16,
                                    '& .MuiChip-label': { px: 0.5 },
                                    pointerEvents: 'none',
                                  }}
                                />
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConversation(conv.participant.id);
                              }}
                              title="Törlés"
                            >
                              <Delete sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < conversations.length - 1 && <Divider sx={{ ml: 9 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}

            {/* Lapozás */}
            {pagination.pages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.pages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      {/* Tömeges művelet megerősítés Dialog */}
      <Dialog 
        open={bulkActionDialogOpen} 
        onClose={() => setBulkActionDialogOpen(false)}
      >
        <DialogTitle>
          Tömeges művelet megerősítése
        </DialogTitle>
        <DialogContent>
          <Typography>
            {bulkAction === 'markRead' && `${selectedConversationIds.size} beszélgetést jelöl olvasottnak?`}
            {bulkAction === 'markUnread' && `${selectedConversationIds.size} beszélgetést jelöl olvasatlannak?`}
            {bulkAction === 'delete' && `Biztosan törli a kijelölt ${selectedConversationIds.size} beszélgetést?`}
          </Typography>
          {bulkAction === 'delete' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ez a művelet nem vonható vissza!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>
            Mégse
          </Button>
          <Button 
            variant="contained" 
            color={bulkAction === 'delete' ? 'error' : 'primary'}
            onClick={handleBulkAction}
          >
            {bulkAction === 'markRead' && 'Olvasottnak jelölés'}
            {bulkAction === 'markUnread' && 'Olvasatlannak jelölés'}
            {bulkAction === 'delete' && 'Törlés'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beszélgetés megtekintése és válaszadás Dialog */}
      <Dialog
        open={conversationDialogOpen}
        onClose={handleCloseConversationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
            {currentParticipant?.userType === 'PROVIDER' && currentParticipant?.provider ? currentParticipant?.provider.businessName : `${currentParticipant?.firstName} ${currentParticipant?.lastName}`}
          </Typography>
          <IconButton onClick={handleCloseConversationDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: '65vh', p: 2 }}>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', pr: 1 }}>
            {currentConversation.map((msg, index) => {
              const isOwnMessage = msg.sender.id === user.id;
              const senderName = isOwnMessage
                ? (user.userType === 'PROVIDER' && user.provider
                    ? user.provider.businessName
                    : `${user.firstName} ${user.lastName}`)
                : (msg.sender.userType === 'PROVIDER' && msg.sender.provider
                    ? msg.sender.provider.businessName
                    : `${msg.sender.firstName} ${msg.sender.lastName}`);

              const bookingStatusLabels = {
                PENDING: 'Ajánlatra vár', OFFER_SENT: 'Ajánlat elküldve',
                OFFER_ACCEPTED: 'Ajánlat elfogadva', OFFER_REJECTED: 'Ajánlat visszautasítva',
                APPROVED: 'Jóváhagyva', REJECTED: 'Elutasítva',
                COMPLETED: 'Befejezett', CANCELLED: 'Lemondva',
              };
              const bookingStatusColors = {
                PENDING: 'default', OFFER_SENT: 'info',
                OFFER_ACCEPTED: 'success', APPROVED: 'success', COMPLETED: 'success',
                OFFER_REJECTED: 'warning', REJECTED: 'error', CANCELLED: 'error',
              };

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                    mb: 1.5
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      maxWidth: '70%',
                      bgcolor: isOwnMessage ? 'primary.light' : 'grey.200',
                      color: isOwnMessage ? 'primary.contrastText' : 'text.primary',
                      borderRadius: '15px',
                      borderBottomLeftRadius: isOwnMessage ? '15px' : '0',
                      borderBottomRightRadius: isOwnMessage ? '0' : '15px',
                    }}
                  >
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {senderName}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.content}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: isOwnMessage ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                      {formatMessageDate(msg.createdAt)}
                    </Typography>
                  </Paper>

                  {/* Foglalás státusz kártya */}
                  {msg.bookingId && (
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        maxWidth: '70%',
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderColor: 'primary.light',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {msg.booking ? (
                        // Gazdag kártya: booking adat elérhető
                        <Box sx={{ p: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            <CalendarToday sx={{ fontSize: 14, color: 'primary.main' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                              {msg.booking.eventType}
                              {msg.booking.eventDate && (
                                <> · {new Date(msg.booking.eventDate).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}</>
                              )}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={bookingStatusLabels[msg.booking.status] || msg.booking.status}
                              color={bookingStatusColors[msg.booking.status] || 'default'}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                            {(msg.booking.finalPrice || msg.booking.offerPrice) && (
                              <Typography variant="caption" color="text.secondary">
                                {(msg.booking.finalPrice || msg.booking.offerPrice).toLocaleString('hu-HU')} Ft
                              </Typography>
                            )}
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                              onClick={() => navigate('/dashboard', { state: { scrollToBooking: msg.booking.id } })}
                              sx={{ ml: 'auto', fontSize: '0.7rem', py: 0, minWidth: 0, color: 'primary.main' }}
                            >
                              Megtekintés
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        // Fallback: csak bookingId van (régi üzenet)
                        <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 14, color: 'primary.main' }} />
                          <Typography variant="caption" color="primary.main" sx={{ flexGrow: 1 }}>
                            Kapcsolódó foglalás
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                            onClick={() => navigate('/dashboard', { state: { scrollToBooking: msg.bookingId } })}
                            sx={{ fontSize: '0.7rem', py: 0, minWidth: 0, color: 'primary.main' }}
                          >
                            Megtekintés
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  )}
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
          <Divider sx={{ my: 1.5 }} />

          {/* Válasz űrlap */}
          <Box sx={{ mt: 'auto' }}>
            {replyError && <Alert severity="error" sx={{ mb: 1 }}>{replyError}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                minRows={1}
                maxRows={2}
                placeholder="Írja be válaszát..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }
                }}
                disabled={replyLoading}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<Reply />}
                onClick={handleReply}
                disabled={replyLoading || !replyContent.trim()}
                sx={{
                  flexShrink: 0
                }}
              >
                {replyLoading ? <CircularProgress size={20} /> : 'Küldés'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {/* Loading overlay */}
      {loading && conversations.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default Messages;